/**
 * Internal platform codename ORS returns in `meta.product` for Atlas surfaces
 * (Projects, Goals, Focus). It is not a customer-facing product name.
 */
export const TOWNSQUARE_CODENAME = 'townsquare';

/**
 * Group heading shown for townsquare (Atlas) links in the byline connections
 * menu. Atlas connections are collapsed under a single "Goals" group; the
 * specific app (data.generator.name) is surfaced per-row in the item metadata.
 */
export const TOWNSQUARE_GROUP_LABEL = 'Goals';

/** True when a link's product is the townsquare codename (case/space-insensitive). */
export const isTownsquareProduct = (product: string | undefined): boolean =>
	product?.toLowerCase().trim() === TOWNSQUARE_CODENAME;

/**
 * Atlas app names that can surface as the product itself — via `data.generator.name`
 * — when the response carries no `townsquare` `meta.product` (e.g. Focus links,
 * which resolve through mercury-object-provider without a product). They collapse
 * under the same "Goals" group as the codename, with the app shown per-row.
 */
const ATLAS_APP_PRODUCTS = new Set(['focus', 'goals', 'projects']);

/**
 * True when a link's product belongs under the collapsed "Goals" group: either the
 * internal `townsquare` codename (Projects/Goals, which set `meta.product`) or an
 * Atlas app name resolved from the JSON-LD generator (Focus).
 */
export const groupsUnderGoals = (product: string | undefined): boolean => {
	if (isTownsquareProduct(product)) {
		return true;
	}
	const normalized = product?.toLowerCase().trim();
	return normalized != null && ATLAS_APP_PRODUCTS.has(normalized);
};
